import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, MapPin, Clock, Users, Briefcase, ArrowRight, Filter, Zap, Star, Building2, X, ExternalLink, Share2, Download, Heart, BookmarkPlus, Linkedin, Mail, Copy, CheckCircle } from 'lucide-react';

// Modal component moved outside to prevent re-creation on every render
const Modal = ({ isOpen, onClose, children, size = 'max-w-4xl' }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={onClose}></div>

                <div className={`inline-block w-full ${size} my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl`}>
                    {children}
                </div>
            </div>
        </div>
    );
};

const PositionsSection = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All positions');
    const [isVisible, setIsVisible] = useState(false);
    const [hoveredJob, setHoveredJob] = useState(null);
    const [selectedJob, setSelectedJob] = useState(null);
    const [showJobModal, setShowJobModal] = useState(false);
    const [showLinkedInModal, setShowLinkedInModal] = useState(false);
    const [showApplicationModal, setShowApplicationModal] = useState(false);
    const [savedJobs, setSavedJobs] = useState(new Set());
    const [applicationData, setApplicationData] = useState({
        name: '',
        email: '',
        phone: '',
        linkedinUrl: '',
        resume: null,
        coverLetter: ''
    });
    const [linkedInFormData, setLinkedInFormData] = useState({
        name: '',
        email: '',
        linkedin: '',
        message: '',
        cvFile: null,
    });
    const [isSubmittingLinkedIn, setIsSubmittingLinkedIn] = useState(false);
    const [isSubmittingApplication, setIsSubmittingApplication] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [jobOpenings, setJobOpenings] = useState([]);
    const [isLoadingJobs, setIsLoadingJobs] = useState(true);
    const [jobCategories, setJobCategories] = useState([{ name: 'All positions', count: 0 }]);
    const sectionRef = useRef(null);

    // Fetch jobs from API
    useEffect(() => {
        const fetchJobs = async () => {
            try {
                setIsLoadingJobs(true);
                const response = await fetch('https://attendanceapi.roadexpressfreight.com/api/job/job-alllistactive');

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                // Map API response to job structure
                const mappedJobs = data.map(job => ({
                    id: job.job_id,
                    title: job.title,
                    location: job.location,
                    type: job.type,
                    experience: job.experience,
                    salary: job.salary,
                    category: job.DepartmentMaster?.department_name || 'General',
                    description: job.description.replace(/<[^>]*>/g, '').substring(0, 300) + '...', // Strip HTML and truncate
                    fullDescription: job.description,
                    skills: job.skills ? job.skills.split(',').map(s => s.trim()) : [],
                    isUrgent: job.is_urgent,
                    department: job.DepartmentMaster?.department_name || 'General',
                    reportingTo: job.DesignationMaster?.designation_name || 'Department Head',
                    postedDate: job.created_at
                }));

                setJobOpenings(mappedJobs);

                // Update categories based on departments
                const departmentCounts = {};
                mappedJobs.forEach(job => {
                    const dept = job.category;
                    departmentCounts[dept] = (departmentCounts[dept] || 0) + 1;
                });

                const categories = [
                    { name: 'All positions', count: mappedJobs.length },
                    ...Object.entries(departmentCounts).map(([name, count]) => ({ name, count }))
                ];

                setJobCategories(categories);
                setIsLoadingJobs(false);
            } catch (error) {
                console.error('Failed to fetch jobs:', error);
                setIsLoadingJobs(false);
                // Fallback to empty array on error
                setJobOpenings([]);
            }
        };

        fetchJobs();
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Close modals on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setShowJobModal(false);
                setShowLinkedInModal(false);
                setShowApplicationModal(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    const submitLinkedInProfile = async (formData) => {
        try {
            const apiFormData = new FormData();
            apiFormData.append('name', formData.name);
            apiFormData.append('email', formData.email);
            apiFormData.append('linkedin', formData.linkedin);
            apiFormData.append('message', formData.message);
            if (formData.cvFile) {
                apiFormData.append('cvFile', formData.cvFile);
            }

            const response = await fetch('https://api.roadexpressfreight.com/api/v1/web/CVshare', {
                method: 'POST',
                body: apiFormData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            return { success: true, data: result };
        } catch (error) {
            console.error('LinkedIn profile submission failed:', error);
            throw error;
        }
    };

    const submitJobApplication = async (formData, jobTitle) => {
        try {
            const apiFormData = new FormData();
            apiFormData.append('name', formData.name);
            apiFormData.append('email', formData.email);
            apiFormData.append('linkedin', formData.linkedinUrl || '');
            apiFormData.append('message', `Job Application for: ${jobTitle}\n\nPhone: ${formData.phone}\n\nCover Letter:\n${formData.coverLetter || 'No cover letter provided.'}`);

            if (formData.resume) {
                apiFormData.append('cvFile', formData.resume);
            }

            const response = await fetch('https://api.roadexpressfreight.com/api/v1/web/CVshare', {
                method: 'POST',
                body: apiFormData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            return { success: true, data: result };
        } catch (error) {
            console.error('Job application submission failed:', error);
            throw error;
        }
    };


    const filteredJobs = jobOpenings.filter(job => {
        const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            job.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'All positions' || job.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const handleJobClick = (job) => {
        setSelectedJob(job);
        setShowJobModal(true);
    };

    const handleSaveJob = (jobId) => {
        const newSavedJobs = new Set(savedJobs);
        if (newSavedJobs.has(jobId)) {
            newSavedJobs.delete(jobId);
        } else {
            newSavedJobs.add(jobId);
        }
        setSavedJobs(newSavedJobs);
    };

    const handleApplyClick = (job) => {
        setSelectedJob(job);
        setShowApplicationModal(true);
    };

    const handleApplicationSubmit = useCallback(async (e) => {
        e.preventDefault();

        if (!applicationData.name || !applicationData.email || !applicationData.phone || !applicationData.resume) {
            return;
        }

        setIsSubmittingApplication(true);

        try {
            await submitJobApplication(applicationData, selectedJob.title);
            setShowApplicationModal(false);
            setShowSuccessMessage(true);
            setTimeout(() => setShowSuccessMessage(false), 5000);
            setApplicationData({
                name: '',
                email: '',
                phone: '',
                linkedinUrl: '',
                resume: null,
                coverLetter: ''
            });
        } catch (error) {
            console.error('Application submission failed:', error);
            // You can add error handling here if needed
        } finally {
            setIsSubmittingApplication(false);
        }
    }, [applicationData, selectedJob]);

    // Optimized input handlers to prevent unnecessary re-renders
    const handleApplicationDataChange = useCallback((field, value) => {
        setApplicationData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleLinkedInFormDataChange = useCallback((field, value) => {
        setLinkedInFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleLinkedInSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!linkedInFormData.name || !linkedInFormData.email || !linkedInFormData.linkedin) {
            return;
        }

        setIsSubmittingLinkedIn(true);
        try {
            await submitLinkedInProfile(linkedInFormData);
            setShowLinkedInModal(false);
            setShowSuccessMessage(true);
            setTimeout(() => setShowSuccessMessage(false), 5000);
            setLinkedInFormData({ name: '', email: '', linkedin: '', message: '', cvFile: null });
        } catch (error) {
            console.error('Submission failed:', error);
        } finally {
            setIsSubmittingLinkedIn(false);
        }
    }, [linkedInFormData]);

    const handleShareJob = async (job) => {
        const shareUrl = `${window.location.origin}/careers/${job.id}`;
        const shareText = `Check out this ${job.title} position at RoadExpress! ${shareUrl}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: `${job.title} - RoadExpress`,
                    text: shareText,
                    url: shareUrl,
                });
            } catch (err) {
                console.log('Error sharing:', err);
            }
        } else {
            // Fallback to copy to clipboard
            await navigator.clipboard.writeText(shareText);
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 2000);
        }
    };

    const handleLinkedInShare = (job) => {
        const shareUrl = `${window.location.origin}/careers/${job.id}`;
        const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        window.open(linkedinUrl, '_blank', 'width=600,height=400');
    };


    return (
        <section
            ref={sectionRef}
            className="w-full bg-gray-100 relative overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

                {/* Success Message */}
                {showSuccessMessage && (
                    <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-4 rounded-xl shadow-lg flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Application submitted successfully!
                    </div>
                )}

                {/* Copy Success Message */}
                {copiedLink && (
                    <div className="fixed top-4 right-4 z-50 bg-blue-500 text-white px-6 py-4 rounded-xl shadow-lg flex items-center">
                        <Copy className="w-5 h-5 mr-2" />
                        Link copied to clipboard!
                    </div>
                )}

                {/* Header Section */}
                <div className="mb-16">
                    <div
                        className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                    >
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-px bg-[#F05A28] mr-4"></div>
                            <span className="text-[#F05A28] font-semibold tracking-wider text-sm uppercase flex items-center">
                                <Zap className="w-4 h-4 mr-2" />
                                Careers
                            </span>
                        </div>

                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                            Job Openings
                        </h1>

                        <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mb-8">
                            Discover exciting career opportunities at RoadExpress and be part of our innovative logistics team.
                        </p>

                        {/* Search Bar */}
                        <div className="relative max-w-2xl">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search for positions..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all text-lg bg-white shadow-sm"
                            />
                        </div>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-12">

                    {/* Sidebar - Categories */}
                    <div className="lg:col-span-3">
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.2s' }}
                        >
                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-8">
                                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                                    <Filter className="w-5 h-5 mr-2 text-[#F05A28]" />
                                    Filter by Category
                                </h3>

                                <div className="space-y-2">
                                    {jobCategories.map((category, index) => (
                                        <button
                                            key={category.name}
                                            onClick={() => setSelectedCategory(category.name)}
                                            className={`w-full flex items-center justify-between p-3 rounded-xl transition-all duration-300 text-left ${selectedCategory === category.name
                                                ? 'bg-[#F05A28] text-white'
                                                : 'hover:bg-gray-50 text-gray-700'
                                                }`}
                                        >
                                            <span className="font-medium">{category.name}</span>
                                            <span className={`text-sm px-2 py-1 rounded-full ${selectedCategory === category.name
                                                ? 'bg-white/20 text-white'
                                                : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                {category.count}
                                            </span>
                                        </button>
                                    ))}
                                </div>

                                {/* Saved Jobs */}
                                {savedJobs.size > 0 && (
                                    <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-200">
                                        <div className="flex items-center mb-2">
                                            <BookmarkPlus className="w-4 h-4 text-blue-600 mr-2" />
                                            <span className="font-semibold text-blue-900">Saved Jobs</span>
                                        </div>
                                        <div className="text-sm text-blue-700">
                                            {savedJobs.size} position{savedJobs.size !== 1 ? 's' : ''} saved
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Job Listings */}
                    <div className="lg:col-span-9">
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.4s' }}
                        >
                            {/* Results Header */}
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">
                                        {filteredJobs.length} Open Positions
                                    </h2>
                                    <p className="text-gray-600 mt-1">
                                        {selectedCategory === 'All positions'
                                            ? 'Showing all available positions'
                                            : `Filtered by ${selectedCategory}`}
                                    </p>
                                </div>
                            </div>

                            {/* Loading State */}
                            {isLoadingJobs && (
                                <div className="flex items-center justify-center py-16">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#F05A28] mx-auto mb-4"></div>
                                        <p className="text-gray-600">Loading job openings...</p>
                                    </div>
                                </div>
                            )}

                            {/* Job Cards */}
                            {!isLoadingJobs && (
                                <div className="space-y-6">
                                    {filteredJobs.map((job, index) => (
                                    <div
                                        key={job.id}
                                        className={`bg-white rounded-2xl border border-gray-100 transition-all duration-500 hover:shadow-xl group ${hoveredJob === job.id ? 'shadow-xl' : 'shadow-sm'
                                            }`}
                                        style={{
                                            transform: `translateY(${isVisible ? '0' : '20px'})`,
                                            opacity: isVisible ? 1 : 0,
                                            transitionDelay: `${0.6 + index * 0.1}s`
                                        }}
                                        onMouseEnter={() => setHoveredJob(job.id)}
                                        onMouseLeave={() => setHoveredJob(null)}
                                    >
                                        <div className="p-8">
                                            <div className="flex items-start justify-between mb-6">
                                                <div className="flex-1">
                                                    <div className="flex items-center mb-3">
                                                        {job.isUrgent && (
                                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 mr-3">
                                                                <Star className="w-3 h-3 mr-1" />
                                                                Urgent
                                                            </span>
                                                        )}
                                                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-orange-50 text-[#F05A28] border border-orange-200">
                                                            {job.category}
                                                        </span>
                                                    </div>

                                                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors cursor-pointer"
                                                        onClick={() => handleJobClick(job)}>
                                                        {job.title}
                                                    </h3>

                                                    <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-4">
                                                        <div className="flex items-center">
                                                            <MapPin className="w-4 h-4 mr-2" />
                                                            <span>{job.location}</span>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <Clock className="w-4 h-4 mr-2" />
                                                            <span>{job.type}</span>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <Users className="w-4 h-4 mr-2" />
                                                            <span>{job.experience}</span>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <Briefcase className="w-4 h-4 mr-2" />
                                                            <span className="font-medium text-green-600">{job.salary}</span>
                                                        </div>
                                                    </div>

                                                    <p className="text-gray-600 leading-relaxed mb-6 line-clamp-3">
                                                        {job.description}
                                                    </p>

                                                    {/* Skills */}
                                                    <div className="flex flex-wrap gap-2 mb-6">
                                                        {job.skills.map((skill, skillIndex) => (
                                                            <span
                                                                key={skillIndex}
                                                                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors"
                                                            >
                                                                {skill}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Save Job Button */}
                                                <button
                                                    onClick={() => handleSaveJob(job.id)}
                                                    className={`ml-4 p-2 rounded-xl transition-all duration-300 ${savedJobs.has(job.id)
                                                        ? 'bg-blue-100 text-blue-600'
                                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                                        }`}
                                                >
                                                    <Heart className={`w-5 h-5 ${savedJobs.has(job.id) ? 'fill-current' : ''}`} />
                                                </button>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-4">
                                                    <div className="text-sm text-gray-500">
                                                        Posted {new Date(job.postedDate).toLocaleDateString()}
                                                    </div>

                                                    {/* Share Button */}
                                                    <button
                                                        onClick={() => handleShareJob(job)}
                                                        className="text-sm text-gray-500 hover:text-[#F05A28] transition-colors flex items-center"
                                                    >
                                                        <Share2 className="w-4 h-4 mr-1" />
                                                        Share
                                                    </button>
                                                </div>

                                                <div className="flex space-x-3">
                                                    <button
                                                        onClick={() => handleJobClick(job)}
                                                        className="border-2 border-[#F05A28] text-[#F05A28] px-6 py-3 rounded-xl font-semibold hover:bg-[#F05A28] hover:text-white transition-all duration-300 flex items-center space-x-2"
                                                    >
                                                        <span>View Details</span>
                                                        <ExternalLink className="w-4 h-4" />
                                                    </button>

                                                    <button
                                                        onClick={() => handleApplyClick(job)}
                                                        className="group bg-[#F05A28] text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition-all duration-300 flex items-center space-x-2 shadow-lg hover:shadow-xl"
                                                    >
                                                        <span>Apply Now</span>
                                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Accent line */}
                                        <div className={`h-1 bg-[#F05A28] rounded-b-2xl transition-all duration-500 origin-left ${hoveredJob === job.id ? 'scale-x-100' : 'scale-x-0'
                                            }`}></div>
                                    </div>
                                    ))}
                                </div>
                            )}

                            {/* No Results */}
                            {!isLoadingJobs && filteredJobs.length === 0 && (
                                <div className="text-center py-16">
                                    <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No positions found</h3>
                                    <p className="text-gray-600">
                                        Try adjusting your search or filter criteria to find more opportunities.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <div
                    className={`mt-20 bg-white rounded-3xl p-12 shadow-sm border border-gray-100 text-center transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                        }`}
                    style={{ transitionDelay: '1s' }}
                >
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                        Don't see the perfect role?
                    </h3>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        We're always looking for talented individuals. Send us your LinkedIn profile and
                        we'll reach out when we have a match.
                    </p>
                    <button
                        onClick={() => setShowLinkedInModal(true)}
                        className="bg-[#F05A28] text-white px-8 py-4 rounded-xl font-semibold hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                        Share your LinkedIn profile
                    </button>
                </div>
            </div>

            {/* Job Details Modal */}
            <Modal isOpen={showJobModal} onClose={() => setShowJobModal(false)} size="max-w-5xl">
                {selectedJob && (
                    <div className="relative">
                        <button
                            onClick={() => setShowJobModal(false)}
                            className="absolute top-6 right-6 p-2 hover:bg-gray-100 rounded-xl transition-colors z-10"
                        >
                            <X className="w-6 h-6 text-gray-500" />
                        </button>

                        <div className="p-8">
                            {/* Header */}
                            <div className="mb-8">
                                <div className="flex items-center mb-4">
                                    {selectedJob.isUrgent && (
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 mr-3">
                                            <Star className="w-3 h-3 mr-1" />
                                            Urgent
                                        </span>
                                    )}
                                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-orange-50 text-[#F05A28] border border-orange-200">
                                        {selectedJob.category}
                                    </span>
                                </div>

                                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                    {selectedJob.title}
                                </h2>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                                        <MapPin className="w-6 h-6 text-[#F05A28] mx-auto mb-2" />
                                        <div className="font-semibold text-gray-900">{selectedJob.location}</div>
                                        <div className="text-sm text-gray-600">Location</div>
                                    </div>
                                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                                        <Clock className="w-6 h-6 text-[#F05A28] mx-auto mb-2" />
                                        <div className="font-semibold text-gray-900">{selectedJob.type}</div>
                                        <div className="text-sm text-gray-600">Employment</div>
                                    </div>
                                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                                        <Users className="w-6 h-6 text-[#F05A28] mx-auto mb-2" />
                                        <div className="font-semibold text-gray-900">{selectedJob.experience}</div>
                                        <div className="text-sm text-gray-600">Experience</div>
                                    </div>
                                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                                        <Briefcase className="w-6 h-6 text-[#F05A28] mx-auto mb-2" />
                                        <div className="font-semibold text-green-600">{selectedJob.salary}</div>
                                        <div className="text-sm text-gray-600">Salary</div>
                                    </div>
                                </div>

                                {/* Skills */}
                                <div className="mb-8">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Required Skills</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedJob.skills.map((skill, index) => (
                                            <span
                                                key={index}
                                                className="px-4 py-2 bg-orange-50 text-[#F05A28] rounded-xl text-sm font-medium border border-orange-200"
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Job Description */}
                            <div className="prose prose-lg max-w-none mb-8">
                                <div
                                    dangerouslySetInnerHTML={{ __html: selectedJob.fullDescription }}
                                    className="text-gray-700 leading-relaxed"
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-between pt-8 border-t border-gray-200">
                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={() => handleSaveJob(selectedJob.id)}
                                        className={`flex items-center px-4 py-2 rounded-xl transition-all duration-300 ${savedJobs.has(selectedJob.id)
                                            ? 'bg-blue-100 text-blue-600'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        <Heart className={`w-4 h-4 mr-2 ${savedJobs.has(selectedJob.id) ? 'fill-current' : ''}`} />
                                        {savedJobs.has(selectedJob.id) ? 'Saved' : 'Save Job'}
                                    </button>

                                    <button
                                        onClick={() => handleLinkedInShare(selectedJob)}
                                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                                    >
                                        <Linkedin className="w-4 h-4 mr-2" />
                                        Share on LinkedIn
                                    </button>
                                </div>

                                <button
                                    onClick={() => {
                                        setShowJobModal(false);
                                        handleApplyClick(selectedJob);
                                    }}
                                    className="bg-[#F05A28] text-white px-8 py-3 rounded-xl font-semibold hover:bg-orange-600 transition-all duration-300 flex items-center space-x-2 shadow-lg"
                                >
                                    <span>Apply for this position</span>
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* LinkedIn Share Modal */}
            <Modal isOpen={showLinkedInModal} onClose={() => setShowLinkedInModal(false)} size="max-w-2xl">
                <div className="relative p-8">
                    <button
                        onClick={() => setShowLinkedInModal(false)}
                        className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                        <X className="w-6 h-6 text-gray-500" />
                    </button>

                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <Linkedin className="w-8 h-8 text-blue-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Share Your LinkedIn Profile</h3>
                        <p className="text-gray-600">
                            Help us understand your background better by sharing your LinkedIn profile.
                            We'll reach out when we have opportunities that match your skills.
                        </p>
                    </div>

                    <form onSubmit={handleLinkedInSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                                Your Name *
                            </label>
                            <input
                                type="text"
                                value={linkedInFormData.name}
                                onChange={(e) => handleLinkedInFormDataChange('name', e.target.value)}
                                placeholder="Full name"
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                                Email Address *
                            </label>
                            <input
                                type="email"
                                value={linkedInFormData.email}
                                onChange={(e) => handleLinkedInFormDataChange('email', e.target.value)}
                                placeholder="your.email@example.com"
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                                LinkedIn Profile URL *
                            </label>
                            <input
                                type="url"
                                value={linkedInFormData.linkedin}
                                onChange={(e) => handleLinkedInFormDataChange('linkedin', e.target.value)}
                                placeholder="https://linkedin.com/in/yourprofile"
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                                CV/Resume
                            </label>
                            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-[#F05A28] transition-colors cursor-pointer">
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => handleLinkedInFormDataChange('cvFile', e.target.files[0])}
                                    className="hidden"
                                    id="linkedin-cv-upload"
                                />
                                <label htmlFor="linkedin-cv-upload" className="cursor-pointer">
                                    <Download className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-gray-600">
                                        Click to upload your CV or drag and drop
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        PDF, DOC, or DOCX up to 5MB
                                    </p>
                                </label>
                                {linkedInFormData.cvFile && (
                                    <p className="text-[#F05A28] mt-2 font-medium">
                                        ✓ {linkedInFormData.cvFile.name}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                                Areas of Interest (Optional)
                            </label>
                            <textarea
                                rows="4"
                                value={linkedInFormData.message}
                                onChange={(e) => handleLinkedInFormDataChange('message', e.target.value)}
                                placeholder="Tell us about the type of roles you're interested in..."
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all resize-none"
                            />
                        </div>

                        <div className="flex items-center justify-between pt-6">
                            <button
                                type="button"
                                onClick={() => setShowLinkedInModal(false)}
                                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-gray-400 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmittingLinkedIn || !linkedInFormData.name || !linkedInFormData.email || !linkedInFormData.linkedin}
                                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg ${isSubmittingLinkedIn || !linkedInFormData.name || !linkedInFormData.email || !linkedInFormData.linkedin
                                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                    : 'bg-[#F05A28] text-white hover:bg-orange-600'
                                    }`}
                            >
                                {isSubmittingLinkedIn ? 'Submitting...' : 'Submit Profile'}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>

            {/* Application Modal */}
            <Modal isOpen={showApplicationModal} onClose={() => setShowApplicationModal(false)} size="max-w-4xl">
                {selectedJob && (
                    <div className="relative">
                        <button
                            onClick={() => setShowApplicationModal(false)}
                            className="absolute top-6 right-6 p-2 hover:bg-gray-100 rounded-xl transition-colors z-10"
                        >
                            <X className="w-6 h-6 text-gray-500" />
                        </button>

                        <div className="p-8">
                            <div className="mb-8">
                                <h3 className="text-3xl font-bold text-gray-900 mb-2">
                                    Apply for {selectedJob.title}
                                </h3>
                                <p className="text-gray-600">
                                    Fill out the application form below and we'll get back to you within 24-48 hours.
                                </p>
                            </div>

                            <form onSubmit={handleApplicationSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={applicationData.name}
                                            onChange={(e) => handleApplicationDataChange('name', e.target.value)}
                                            placeholder="Your full name"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            value={applicationData.email}
                                            onChange={(e) => handleApplicationDataChange('email', e.target.value)}
                                            placeholder="your.email@example.com"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Phone Number *
                                        </label>
                                        <input
                                            type="tel"
                                            value={applicationData.phone}
                                            onChange={(e) => handleApplicationDataChange('phone', e.target.value)}
                                            placeholder="+91 9876543210"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            LinkedIn Profile
                                        </label>
                                        <input
                                            type="url"
                                            value={applicationData.linkedinUrl}
                                            onChange={(e) => handleApplicationDataChange('linkedinUrl', e.target.value)}
                                            placeholder="https://linkedin.com/in/yourprofile"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        Resume/CV *
                                    </label>
                                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-[#F05A28] transition-colors cursor-pointer">
                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx"
                                            onChange={(e) => handleApplicationDataChange('resume', e.target.files[0])}
                                            className="hidden"
                                            id="resume-upload"
                                        />
                                        <label htmlFor="resume-upload" className="cursor-pointer">
                                            <Download className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-gray-600">
                                                Click to upload your resume or drag and drop
                                            </p>
                                            <p className="text-sm text-gray-500 mt-1">
                                                PDF, DOC, or DOCX up to 10MB
                                            </p>
                                        </label>
                                        {applicationData.resume && (
                                            <p className="text-[#F05A28] mt-2 font-medium">
                                                ✓ {applicationData.resume.name}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        Cover Letter
                                    </label>
                                    <textarea
                                        rows="6"
                                        value={applicationData.coverLetter}
                                        onChange={(e) => handleApplicationDataChange('coverLetter', e.target.value)}
                                        placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all resize-none"
                                    />
                                </div>

                                <div className="flex items-center justify-between pt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowApplicationModal(false)}
                                        className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-gray-400 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmittingApplication || !applicationData.name || !applicationData.email || !applicationData.phone || !applicationData.resume}
                                        className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 shadow-lg ${isSubmittingApplication || !applicationData.name || !applicationData.email || !applicationData.phone || !applicationData.resume
                                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                            : 'bg-[#F05A28] text-white hover:bg-orange-600'
                                            }`}
                                    >
                                        <span>{isSubmittingApplication ? 'Submitting...' : 'Submit Application'}</span>
                                        {!isSubmittingApplication && <ArrowRight className="w-5 h-5" />}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </Modal>
        </section>
    );
};

export default PositionsSection;